import React, { useMemo, useState } from "react";
import { formatCurrency, formatDate, formatNumber, isEmptyValue, prettyLabel, toYN } from "./utils/formatters";
import { Check, Copy } from "feather-icons-react";
import { Document, Page, StyleSheet, pdf as renderPdf, View } from "@react-pdf/renderer";
import axios from "axios";
import VehicleSchedulePdf from "../reviewQuote/pdf/VehiclesSchedule";
import TrailersSchedulePdf from "../reviewQuote/pdf/TrailersSchedule";
import DriverSchedulePdf from "../reviewQuote/pdf/DriverSchedule";

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-300">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
}

function CopyButton({ label, value }) {
  const [copied, setCopied] = useState(false);
  const displayValue = value?.toString() || "N/A";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(displayValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={handleCopy}
          className="flex-shrink-0 p-2 rounded-md hover:bg-gray-100 transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4 text-gray-600" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            {label}
          </p>
          <p className="text-sm font-semibold text-gray-900 break-all">
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, format = "raw" }) => {
  let display = value;

  switch (format) {
    case "currency":
      display = formatCurrency(value);
      break;
    case "number":
      display = formatNumber(value);
      break;
    case "date":
      display = formatDate(value);
      break;
    case "yn":
      display = toYN(value);
      break;
    case "percentage":
      display = `${value}%`;
      break;
    default:
      display = isEmptyValue(value) ? "N/A" : value;
  }

  return <CopyButton label={label} value={display} />;
};

/** ---------- Label maps for enum-ish values coming from Step8 ---------- */
const AL_LIMIT_LABELS = {
  "25k-50k": "$ 25,000 / $ 50,000",
  "50k-100k": "$ 50,000 / $ 100,000",
  "100k_csl": "$ 100,000 CSL",
  "100k-300k": "$ 100,000 / $ 300,000",
  "300k_csl": "$ 300,000 CSL",
  "250k-500k": "$ 250,000 / $ 500,000",
  "500k_csl": "$ 500,000 CSL",
  "750k_csl": "$ 750,000 CSL",
  "1m_csl": "$ 1,000,000 CSL",
};

const limitAutoLiabilityOptionsForCoverWhaleAndNirvanaLabels = {
  '1000000': '$ 1,000,000',
  '750000': '$ 750,000',
};

const limitAutoLiabilityOptionsForProgressiveLabels = {
  '750000': '$ 750,000',
  '1000000': '$ 1,000,000',
  '1500000': '$ 1,500,000',
  '2000000': '$ 2,000,000',
};

const NON_OWNED_TRAILER_TYPE = {
  dry_freight: "Dry Freight",
  flatbed: "Flatbed",
  refer: "Reefer",
  gooseneck: "Gooseneck",
};



/** Render helpers */
const maybe = (cond, node) => (!!cond ? node : null);
const nonEmpty = (v) => !isEmptyValue(v);

const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
  },
  combinedPage: {
    alignItems: "stretch",
  },
  combinedColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
});

const scheduleFileName = (label) => `${label.replace(/\s+/g, "-").toLowerCase()}-schedule.pdf`;

async function downloadSchedulePdf(component, fileLabel) {
  const doc = (
    <Document title={fileLabel}>
      <Page size="A4" style={pdfStyles.page}>
        {component}
      </Page>
    </Document>
  );

  const blob = await renderPdf(doc).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = scheduleFileName(fileLabel);
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

async function downloadVehicleTrailerSchedulePdf(vehicles, trailers) {
  const fileLabel = "Vehicle Trailer Schedule";
  const doc = (
    <Document title={fileLabel}>
      <Page size="A4" style={[pdfStyles.page, pdfStyles.combinedPage]}>
        <View style={pdfStyles.combinedColumn}>
          <VehicleSchedulePdf vehicles={vehicles} />
          <TrailersSchedulePdf trailers={trailers} />
        </View>
      </Page>
    </Document>
  );

  const blob = await renderPdf(doc).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = scheduleFileName(fileLabel);
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

const TELEMETRY_OPTIONS = {
  eld_only: "ELD Only",
  dash_cam_dual_ai_connected: "Dash Camera",
};

function DocumentsSection({ q }) {
  const carriers = useMemo(() => {
    const arr = Array.isArray(q?.carrier) ? q?.carrier : q?.carrier ? [q?.carrier] : [];
    const quoteArr = Array.isArray(q?.quote?.step1?.carrier) ? q?.quote?.step1?.carrier : q?.quote?.step1?.carrier ? [q?.quote?.step1?.carrier] : [];
    return [...arr, ...quoteArr].filter(Boolean);
  }, [q?.carrier, q?.quote?.step1?.carrier]);

  const hasAccelerant = carriers.some((c) => String(c).toLowerCase() === "accelerant");
  if (!hasAccelerant) return null;

  const uploadedDocs = q?.quote?.step10?.documents ?? q?.quote?.documents ?? q?.documents ?? [];
  const vehicles = q?.quote?.vehicles ?? [];
  const trailers = q?.quote?.trailers ?? [];
  const drivers = q?.quote?.drivers ?? [];

  const handleDocDownload = async (doc) => {
    const href = doc.s3_link || doc.url;
    if (!href) return;
    if (href.startsWith("/api/document/")) {
      try {
        const resp = await axios.get(href);
        const direct = resp?.data?.link || href;
        window.open(direct, "_blank", "noopener,noreferrer");
        return;
      } catch (err) {
        console.error("Failed to resolve document link", err);
      }
    }
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (type) => {
    if (type === "vehiclesAndTrailers") {
      await downloadVehicleTrailerSchedulePdf(vehicles, trailers);
    } else if (type === "drivers") {
      await downloadSchedulePdf(<DriverSchedulePdf drivers={drivers} />, "Driver Schedule");
    }
  };

  return (
    <Section title="Documents (Accelerant)">
      <div className="col-span-full bg-white border rounded-lg p-4 space-y-4">
        <div className="text-sm font-semibold">Uploaded Documents</div>
        {uploadedDocs.length === 0 && (
          <p className="text-sm text-gray-600">No documents were uploaded in Step 10.</p>
        )}
        <ul className="space-y-2">
          {uploadedDocs.map((doc, idx) => (
            <li key={`${doc.type}-${idx}`} className="flex items-center justify-between gap-3">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-900">{(doc.name || doc.type || "").toString().toUpperCase()}</span>
                <span className="text-xs text-gray-500">{doc.type}</span>
              </div>
              {doc.s3_link || doc.url ? (
                <button
                  type="button"
                  className="text-blue-600 text-sm underline break-all"
                  onClick={() => handleDocDownload(doc)}
                >
                  Download
                </button>
              ) : (
                <span className="text-xs text-gray-500">No link available</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="col-span-full bg-white border rounded-lg p-4 space-y-3">
        <div className="text-sm font-semibold">Schedules</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => handleDownload("vehiclesAndTrailers")}
            className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 text-sm"
          >
            Download Vehicle & Trailer Schedule
          </button>
          <button
            onClick={() => handleDownload("drivers")}
            className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 text-sm"
          >
            Download Driver Schedule
          </button>
        </div>
      </div>
    </Section>
  );
}

function QuoteDetails({ quoteData, selectedCarrier }) {
  const quote = quoteData?.quote ?? {};
  const step1 = quoteData?.quote?.step1 ?? {};
  const topLevelOps = quoteData?.operations ?? {};
  const insuredInformation = quote?.insuredInformation ?? {};
  const mailingAddress = quote?.mailingAddress ?? {};
  const garageAddress = quote?.garageAddress ?? {};
  const shippingAddress = quote?.shippingAddress ?? {};
  const owner = insuredInformation.owner ?? {};
  const insuredPhone = insuredInformation.insuranceContactPhone || owner?.phone;
  const operations = quote?.operations ?? {};
  const drivers = quote?.drivers ?? [];
  const driverTraining = quote?.step5 ?? {};
  const vehicles = quote?.vehicles ?? [];
  const trailers = quote?.trailers ?? [];
  const coverage = quote?.coverage ?? {};
  const limits = quote?.limits ?? {};
  const radius = quote?.radius ?? {};
  const commodities = Array.isArray(quote?.commodities) ? quote.commodities : [];
  const stateMileage = Array.isArray(quote?.stateMileage) ? quote.stateMileage : [];
  const nonOwnedTrailers = Array.isArray(coverage?.nonOwnedTrailers) ? coverage.nonOwnedTrailers : [];
  const losses = quote?.losses ?? {};
  const lossPeriods = [losses["1"], losses["2"], losses["3"]].filter(Boolean);
  const carrier = (selectedCarrier || "").toLowerCase();
  const driverCount = Array.isArray(drivers) ? drivers.length : 0;
  const timeInBusiness = (() => {
    const yearsVal =
      Number(insuredInformation.yearsInBusiness ?? quote?.yearsInBusiness ?? quote?.years_in_business) || 0;
    const monthsVal = Number(insuredInformation.monthsInBusiness) || 0;
    const monthsTotal = yearsVal * 12 + monthsVal;

    const years = Math.floor(monthsTotal / 12);
    const remainingMonths = monthsTotal % 12;

    return `${years} year${years !== 1 ? "s" : ""} ${remainingMonths} month${remainingMonths !== 1 ? "s" : ""}`;
  })();
  const garageStreet = `${garageAddress.garageStreet || ""} ${garageAddress.garageStreet2 || ""}`.trim();
  const policyDate = (quoteData?.effectiveDate || quoteData?.effective_date)?.toString().split("T")[0] || "";
  const carriersList = Array.isArray(quoteData?.carrier) ? quoteData?.carrier : quoteData?.carrier ? [quoteData?.carrier] : [];
  const hasAL = coverage.requestAl;
  const hasAPD = coverage.requestApd;
  const hasMTC = coverage.requestMtc;
  const hasTGL = coverage.requestTgl;
  const hasNTL = coverage.requestNtl;
  const normalizeYnValue = (val) => {
    if (val === true) return "Y";
    if (val === false) return "N";
    if (typeof val === "string") {
      const lower = val.toLowerCase();
      if (lower === "y" || lower === "yes") return "Y";
      if (lower === "n" || lower === "no") return "N";
    }
    return val;
  };
  const trailerInterchangeValue = normalizeYnValue(
    coverage.trailer_interchange ??
    coverage.trailerInterchange ??
    limits?.trailerInterchange
  );
  const hasTI = trailerInterchangeValue === "Y";
  const hasRefrigeratedCommodity =
    quote?.commoditiesRefrigeration === "Y" ||
    commodities.some((c) =>
      c.refrigerated === true ||
      c.refrigerated === "Y" ||
      String(c.commodityKey || c.key || "").toLowerCase().includes("refriger")
    );
  const filesDot = operations?.vehicleFilesDotCompliant ?? quote?.vehicleFilesDotCompliant;
  const personalUse = operations?.personalUseOfVehicle ?? quote?.personalUse;
  const opsFlags = {
    "Local trucker": operations.opsLocal,
    "Intermediate": operations.opsIntermediate,
    "Long haul trucker": operations.opsLongHaul,
    "Intermodal or port": operations.opsIntermodalPort,
    "Dump Trucks (Other)": operations.opsDumpTruckOther,
    "Dump Trucks (Dirt, Sand and Gravel)": operations.opsDumpTruckSandGravel,
    "End dumper units": operations.opsEndDumper,
    "Logging": operations.opsLogging,
    "Refrigerated Trailer": operations.opsRefrigirated,
    "Hot shot (TGL and NTL not available for class 3 vehicles)": operations.opsHotShot,
    "Oversized or overweight": operations.opsOversizedOvernight,
    "Automobile hauler [Excluded for cargo and NTL]": operations.opsAutomobileHauler,
    "Household goods mover": operations.opsHouseholdGoods,
    "Tankers": operations.opsTanker,
    "UIIA - Intermodal": operations.UIIAintermodal,
  };
  const bestOperationDescription =
    Object.entries(opsFlags)
      .filter(([, v]) => v === "Y" || v === true)
      .map(([label]) => label)
      .join(", ") || "N/A";

  const carriers = useMemo(() => {
    const carrierList = Array.isArray(quoteData?.carrier) ? quoteData?.carrier : quoteData?.carrier ? [quoteData?.carrier] : [];
    const step1CarrierList = Array.isArray(quoteData?.quote?.step1?.carrier) ? quoteData?.quote?.step1?.carrier : quoteData?.quote?.step1?.carrier ? [quoteData?.quote?.step1?.carrier] : [];
    return [...carrierList, ...step1CarrierList].filter(Boolean);
  }, [quoteData?.carrier, quoteData?.quote?.step1?.carrier]);

  const hasAccelerant = carriers.some((c) => String(c).toLowerCase() === "accelerant");

  const uploadedDocs = quote?.step10?.documents ?? quote?.documents ?? quoteData?.documents ?? [];

  const handleDocDownload = async (doc) => {
    const href = doc.s3_link || doc.url;
    if (!href) return;
    if (href.startsWith("/api/document/")) {
      try {
        const resp = await axios.get(href);
        const direct = resp?.data?.link || href;
        window.open(direct, "_blank", "noopener,noreferrer");
        return;
      } catch (err) {
        console.error("Failed to resolve document link", err);
      }
    }
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (type) => {
    if (type === "vehiclesAndTrailers") {
      await downloadVehicleTrailerSchedulePdf(vehicles, trailers);
    } else if (type === "drivers") {
      await downloadSchedulePdf(<DriverSchedulePdf drivers={drivers} />, "Driver Schedule");
    }
  };

  const renderCommodityBlocks = () => (
    Array.isArray(commodities) && commodities.length > 0 && (
      <div className="col-span-full space-y-3">
        {commodities.map((commodity, index) => (
          <div key={`${commodity.commodityKey || commodity.key}-${index}`} className="bg-gray-50 p-3 rounded border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label={`Commodity ${index + 1} Type`} value={prettyLabel(commodity.commodityKey || commodity.key)} />
              <Field label={`Commodity ${index + 1} Percentage`} value={commodity.commodityPercentage} format="number" />
              {commodity.generalMerchDetail && (
                <Field label="Details" value={commodity.generalMerchDetail} />
              )}
            </div>
          </div>
        ))}
      </div>
    )
  );

  const renderStateMileageBlocks = () => (
    stateMileage.length > 0 && (
      <div className="col-span-full space-y-3">
        {stateMileage.map((state, index) => (
          <div key={`${state.state}-${index}`} className="bg-gray-50 p-3 rounded border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label="State" value={state.state} />
              <Field label="Percentage" value={state.percentage} format="number" />
              {state.miles != null && state.miles !== "" && (
                <Field label="Miles" value={state.miles} format="number" />
              )}
            </div>
          </div>
        ))}
      </div>
    )
  );

  const renderLossBlocks = () => (
    lossPeriods.length > 0 && (
      <Section title="Losses">
        <div className="col-span-full space-y-3">
          {lossPeriods.map((period, index) => (
            <div key={index} className="bg-white border rounded-lg p-4 space-y-3">
              <div className="text-sm font-semibold">Period {index + 1}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Auto Liability Count" value={period.lossAlCount} format="number" />
                <Field label="Auto Liability Paid" value={period.lossAlPaid} format="currency" />
                <Field label="Physical Damage Count" value={period.lossApdCount} format="number" />
                <Field label="Physical Damage Paid" value={period.lossApdPaid} format="currency" />
                <Field label="Cargo Count" value={period.lossMtcCount} format="number" />
                <Field label="Cargo Paid" value={period.lossMtcPaid} format="currency" />
                <Field label="TGL Count" value={period.lossTglCount} format="number" />
                <Field label="TGL Paid" value={period.lossTglPaid} format="currency" />
              </div>
            </div>
          ))}
        </div>
      </Section>
    )
  );

  if (carrier === "accelerant") {
    return (
      <div className="space-y-8">
        <Section title="Accelerant">
          <Field label="Producer" value={quoteData?.producerData?.name || quoteData?.quote?.producer} />
          <Field label="Agent Email" value={quoteData?.agent_email || step1.agentEmail} />
          <Field label="Carrier(s)" value={carriersList.join(", ")} />
          <Field label="Garaging Address Zip" value={garageAddress.garageZip} />
          <Field label="Legal Name" value={insuredInformation.legalName} />
          <Field label="DBA" value={insuredInformation.dbaName} />
          <Field label="FEIN" value={insuredInformation.fein} />
          <Field label="Entity Type" value={insuredInformation.entityType} />
          <Field label="Time In Business" value={timeInBusiness} />
          <Field label="Number of Drivers Count" value={driverCount} format="number" />
          <Field label="Garaging Address (Street)" value={garageStreet} />
          <Field label="Garaging Address (City)" value={garageAddress.garageCity} />
          <Field label="Garaging Address (State)" value={garageAddress.garageState} />
          <Field label="Garaging Address (Zip)" value={garageAddress.garageZip} />
          <Field label="Policy Date" value={policyDate} />
          <Field label="MC Number" value={insuredInformation.motorCarrierNumber} />
          <Field label="DOT Number" value={insuredInformation.dotNumber} />
          <Field label="OPERATIONS DESCRIPTION" value={bestOperationDescription} />
          <Field label="Waiver of Subrogation" value={limits?.insuredWaiverSubrogation === "Y"} format="yn" />
          <Field label="Primary & Noncontributory (AL)" value={limits?.primaryNoncontributoryAL === "Y"} format="yn" />
          <Field label="Filings AL Federal" value={operations.filingsAlFederal === "Y"} format="yn" />
          <Field label="Filings AL State" value={operations.filingsAlState === "Y"} format="yn" />
          <Field label="Miles (Projected)" value={insuredInformation.miles?.projected} format="number" />
          <Field label="Revenue (Projected)" value={insuredInformation.revenue?.projected} format="currency" />
          <Field label="Trucks (Count)" value={limits?.nbrOfTrucks} format="number" />
          <Field label="Miles (Current)" value={insuredInformation.miles?.current} format="number" />
          <Field label="Revenue (Current)" value={insuredInformation.revenue?.current} format="currency" />
          <Field label="Miles (1st Prior)" value={insuredInformation.miles?.firstPrior} format="number" />
          <Field label="Revenue (1st Prior)" value={insuredInformation.revenue?.firstPrior} format="currency" />
          <Field label="Miles (2nd Prior)" value={insuredInformation.miles?.secondPrior} format="number" />
          <Field label="Revenue (2nd Prior)" value={insuredInformation.revenue?.secondPrior} format="currency" />
        </Section>

        {commodities.length > 0 && (
          <Section title="Commodities">
            {renderCommodityBlocks()}
          </Section>
        )}

        <Section title="Coverages">
          <Field label="(Auto Liability) Requested" value={hasAL} format="yn" />
          {maybe(hasAL, <Field label="Auto Liability Limit for Cover Whale / Nirvana / Accelerant" value={limitAutoLiabilityOptionsForCoverWhaleAndNirvanaLabels[limits?.limitAutoLiability]} format="currency" />)}
          {maybe(hasAL, <Field label="PIP" value={coverage.optAlPip} format="yn" />)}
          {maybe(hasAL, <Field label="UM/UIM Property Damage" value={coverage.optAlUm} format="yn" />)}
          <Field label="(Automobile Physical Damage (APD)) Requested" value={hasAPD} format="yn" />
          {maybe(hasAPD, <Field label="Comp/Collision Deductible Selected" value={coverage.compCollisionDeductibleChecked} format="yn" />)}
          <Field label="(Trailer Interchange) Requested" value={trailerInterchangeValue} format="yn" />
        </Section>

        <Section title="Radius & Mileage">
          <Field label="0–50 miles" value={radius?.radius0_50} format="percentage" />
          <Field label="51–200 miles" value={radius?.radius51_200} format="percentage" />
          <Field label="201–500 miles" value={radius?.radius201_500} format="percentage" />
          <Field label="500+ miles" value={radius?.radius501} format="percentage" />
        </Section>

        {stateMileage.length > 0 && (
          <Section title="State Mileage">
            {renderStateMileageBlocks()}
          </Section>
        )}

        <Section title="Operations & Safety">
          <Field label="Subsidiary" value={operations.subsidiary === "Y"} format="yn" />
          <Field label="Oversized Shipments" value={operations.oversizedShipments === "Y"} format="yn" />
          <Field label="Haul Double/Triple Trailers" value={step1.haulDoubleTripleTrailers} format="yn" />
          <Field label="Brokerage Authority" value={operations.brokerageAuthority === "Y"} format="yn" />
          <Field label="Personal Use" value={personalUse} format="yn" />
          <Field label="Owner Operators" value={operations.ownerOperators === "Y"} format="yn" />
          <Field label="Trip Leasing" value={operations.tripLeasingUnderAuthority === "Y"} format="yn" />
          <Field label="Interline Agreement" value={operations.interlineAgreement === "Y"} format="yn" />
          <Field label="Interchange Agreement" value={operations.interchangeAgreement === "Y"} format="yn" />
          <Field label="Intermodal Agreement" value={operations.intermodalAgreement === "Y"} format="yn" />
          <Field label="Driver Home Days/Week" value={driverTraining.driverHomeDaysPerWeek} format="number" />
          <Field label="Minimum Age of Drivers" value={driverTraining.minimumAgeOfDrivers} format="number" />
          <Field label="Minimum CDL Experience Years" value={driverTraining.cdlExperience?.year} />
          <Field label="Drivers DOT Compliant" value={driverTraining.driversDotCompliant} format="yn" />
          <Field label="Company Rules/Procedures" value={driverTraining.companyRulesProcedures} format="yn" />
          <Field label="Daily Vehicle Inspections" value={driverTraining.dailyVehicleInspections} format="yn" />
          <Field label="Equipment Familiarization" value={driverTraining.equipmentFamiliarization} format="yn" />
          <Field label="Route Familiarization" value={driverTraining.routeFamiliarization} format="yn" />
          <Field label="Emergency Procedures" value={driverTraining.emergencyProcedures} format="yn" />
          <Field label="Accident Reporting Procedures" value={driverTraining.accidentReportingProcedures} format="yn" />
          <Field label="MVRs Checked Annually" value={driverTraining.mvrsCheckedAnnually} format="yn" />
          <Field label="Maximum Violations Past 36 Months" value={driverTraining.violationsPastSixMonths} format="number" />
          <Field label="Zero Tolerance Policy" value={driverTraining.zeroTolerancePolicyForMajorViolations} format="yn" />
          <Field label="Disciplinary Action" value={driverTraining.disciplinaryActionForDrivers} />
          <Field label="Written Driver Safety Manual" value={driverTraining.writtenDriverSafetyManual} format="yn" />
          <Field label="Safety Rewards Program" value={driverTraining.safetyRewardsProgram} format="yn" />
          <Field label="Mandatory Safety Meetings Held" value={driverTraining.howOftenAreMandatorySafetyMeetingsHeld} />
          <Field label="Maintain Accident Register" value={driverTraining.maintainAccidentRegister} format="yn" />
          <Field label="Written Maintenance Program" value={driverTraining.writtenMaintenanceProgram} format="yn" />
          <Field label="Insured Does Own Maintenance" value={driverTraining.insuredDoTheirOwnMaintenance} format="yn" />
          <Field label="Vehicle Files DOT Compliant" value={filesDot} format="yn" />
        </Section>

        <Section title="Contacts">
        <Field label="Insurance Contact First Name" value={insuredInformation.insuranceContactFirstName} />
        <Field label="Insurance Contact Last Name" value={insuredInformation.insuranceContactLastName} />
        <Field label="Insurance Contact Email" value={insuredInformation.insuranceContactEmail} />
        <Field label="Insurance Contact Phone" value={insuredPhone} />
      </Section>


        <DocumentsSection q={quoteData} />
      </div>
    );
  }

  if (carrier === "progressive") {
    return (
      <div className="space-y-8">
        <Section title="Progressive">
          <Field label="Producer" value={quoteData?.producerData?.name || quoteData?.quote?.producer} />
          <Field label="Agent Email" value={quoteData?.agent_email || step1.agentEmail} />
          <Field label="Carrier(s)" value={carriersList.join(", ")} />
          <Field label="Policy Date" value={policyDate} />
          <Field label="Currently Insured with Progressive" value={step1.insuredWithProgressiveCommercialAuto} format="yn" />
          <Field label="DOT Number" value={insuredInformation.dotNumber} />
          <Field label="DOT NUMBER ISSUED IN THE LAST 60 DAYS" value={insuredInformation.insuredIssuedWithInLastSixtyDays} format="yn" />
          <Field label="OPERATIONS DESCRIPTION" value={bestOperationDescription} />
          <Field label="Entity Type" value={insuredInformation.entityType} />
          <Field label="BUSINESS TYPE" value={owner.businessType} />
          <Field label="Legal Name" value={insuredInformation.legalName} />
          <Field label="DBA NAME" value={insuredInformation.dbaName} />
          <Field label="Owner Information First Name" value={owner.firstName} />
          <Field label="Owner Information Last Name" value={owner.lastName} />
          <Field label="Owner Information Street" value={`${owner.address?.address1 || ""} ${owner.address?.address2 || ""}`.trim()} />
          <Field label="Owner Information City" value={owner.address?.city} />
          <Field label="Owner Information State" value={owner.address?.state} />
          <Field label="Owner Information Zip" value={owner.address?.zip} />
          <Field label="Owner Information Date of Birth" value={owner.dob} format="date" />
          <Field label="Hazmat Placard" value={step1.hazmatPlacard} format="yn" />
        </Section>

        {vehicles.length > 0 && (
          <Section title="Vehicles">
            {vehicles.map((vehicle, index) => (
              <div key={vehicle.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border space-y-2">
                <Field label={`Vehicle ${index + 1} VIN`} value={vehicle.vin} />
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Year" value={vehicle.year} />
                  <Field label="Make" value={vehicle.make} />
                  <Field label="Model" value={vehicle.model} />
                  <Field label="Value" value={vehicle.value} format="currency" />
                  <Field label="Body Type" value={vehicle.bodyTypeKey || vehicle.bodyType} />
                  <Field label="Class" value={vehicle.classKey || vehicle.class} />
                  <Field label="Include APD Towing" value={vehicle.includeAPDTowing ?? vehicle.includeApdTowing} format="yn" />
                  <Field label="Hitch Type" value={vehicle.hitchType} />
                  <Field label="Garaging ZIP" value={garageAddress.garageZip} />
                </div>
              </div>
            ))}
          </Section>
        )}

        <Section title="Radius">
          <Field label="0–50 miles" value={radius?.radius0_50} format="percentage" />
          <Field label="51–200 miles" value={radius?.radius51_200} format="percentage" />
          <Field label="201–500 miles" value={radius?.radius201_500} format="percentage" />
          <Field label="500+ miles" value={radius?.radius501} format="percentage" />
        </Section>

        {trailers.length > 0 && (
          <Section title="Trailers">
            {trailers.map((trailer, index) => (
              <div key={trailer.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border space-y-2">
                <Field label={`Trailer ${index + 1} VIN`} value={trailer.vin} />
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Value" value={trailer.value} format="currency" />
                  <Field label="Year" value={trailer.year} />
                  <Field label="Make" value={trailer.make} />
                  <Field label="Model" value={trailer.model} />
                  <Field label="BODY TYPE" value={trailer.bodyTypeKey || trailer.bodyType} />
                  <Field label="Zip" value={garageAddress.garageZip} />
                </div>
              </div>
            ))}
          </Section>
        )}

        {drivers.length > 0 && (
          <Section title="Drivers">
            {drivers.map((driver, index) => (
              <div key={driver.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label={`Driver ${index + 1} First Name`} value={driver.firstName} />
                  <Field label={`Driver ${index + 1} Last Name`} value={driver.lastName} />
                  <Field label={`Driver ${index + 1} Date of Birth`} value={driver.dateOfBirth} />
                  <Field label={`Driver ${index + 1} License State`} value={driver.licenseState} />
                  <Field label={`Driver ${index + 1} License Number`} value={driver.licenseNumber} />
                </div>
              </div>
            ))}
          </Section>
        )}

        <Section title="Contacts & History">
          <Field label="Insurance Contact Email" value={insuredInformation.insuranceContactEmail} />
          <Field label="INSURED PHONE NUMBER" value={insuredPhone} />
          <Field label="Currently Insured" value={coverage.currentlyInsured} format="yn" />
          <Field label="Continuous Coverage" value={coverage.continuousCoverage} format="yn" />
          <Field label="Current BI Limit (Checked)" value={coverage.currentBILimitChecked} format="yn" />
          <Field label="Blanket Additional Insured" value={coverage.blanketAdditionalInsured === "yes" || coverage.blanketAdditionalInsured === true || coverage.blanketAdditionalInsured === "Y"} format="yn" />
          <Field label="Waiver of Subrogation" value={limits?.insuredWaiverSubrogation === "Y"} format="yn" />
          <Field label="Filings AL Federal" value={operations.filingsAlFederal === "Y"} format="yn" />
          <Field label="Filings AL State" value={operations.filingsAlState === "Y"} format="yn" />
          <Field label="MC Number" value={insuredInformation.motorCarrierNumber} />
        </Section>

        <Section title="Coverages">
          <Field label="(Auto Liability) Requested" value={hasAL} format="yn" />
          {maybe(hasAL, <Field label="PIP" value={coverage.optAlPip} format="yn" />)}
          {maybe(hasAL, <Field label="UM/UIM Property Damage" value={coverage.optAlUm} format="yn" />)}
          {maybe(hasAL, <Field label="Auto Liability Limit for Progressive" value={limitAutoLiabilityOptionsForProgressiveLabels[limits?.autoLiabilityLimitForProgressive]} format="currency" />)}
          {maybe(hasAPD, <Field label="Comp/Collision Deductible Selected" value={coverage.compCollisionDeductibleChecked} format="yn" />)}
          {maybe(hasAPD, <Field label="COMP/COLLISION DEDUCTIBLE AMOUNT" value={coverage.compCollisionDeductibleOption} format="currency" />)}
          {maybe(hasAPD, <Field label="Rental Reimbursement" value={coverage.rentalReimbursement === "yes" || coverage.rentalReimbursement === true || coverage.rentalReimbursement === "Y"} format="yn" />)}
          {maybe(hasAPD, <Field label="Roadside Assistance" value={coverage.roadsideAssistance === "yes" || coverage.roadsideAssistance === true || coverage.roadsideAssistance === "Y"} format="yn" />)}
          <Field label="(Hired Auto Liability) Selected" value={coverage.hiredAutoLiability} format="yn" />
          {maybe(coverage.hiredAutoLiability, <Field label="(Hired Auto Liability) Count (Past Year)" value={coverage.hiredAutoCount} format="number" />)}
          <Field label="(Trailer Interchange) Requested" value={trailerInterchangeValue} format="yn" />
          <Field label="(Motor Truck Cargo) Requested" value={hasMTC} format="yn" />
          {maybe(hasMTC, <Field label="Limit (MTC)" value={limits?.mtcLimit || coverage.motor_truck_cargo_limit} format="currency" />)}
          {maybe(hasMTC && coverage.mtc_deductible, <Field label="(Motor Truck Cargo) Deductible" value={coverage.mtc_deductible} format="currency" />)}
          <Field label="(Automobile Physical Damage) Requested" value={hasAPD} format="yn" />
          <Field label="(Truckers General Liability) Requested" value={hasTGL} format="yn" />
          <Field label="(Non-Trucking Liability) Requested" value={hasNTL} format="yn" />
          {maybe(hasTGL, <Field label="(Truckers General Liability) Primary & Noncontributory (TGL)" value={limits?.primaryNoncontributoryTGL === "Y"} format="yn" />)}
          <Field label="Refrigeration Coverage Required" value={hasRefrigeratedCommodity} format="yn" />
        </Section>

        {commodities.length > 0 && (
          <Section title="Commodities">
            {renderCommodityBlocks()}
          </Section>
        )}
      </div>
    );
  }

  if (carrier === "nirvana") {
    return (
      <div className="space-y-8">
        <Section title="Nirvana">
          <Field label="Producer" value={quoteData?.producerData?.name || quoteData?.quote?.producer} />
          <Field label="Agent Email" value={quoteData?.agent_email || step1.agentEmail} />
          <Field label="Carrier(s)" value={carriersList.join(", ")} />
          <Field label="DOT Number" value={insuredInformation.dotNumber} />
          <Field label="OPERATIONS DESCRIPTION" value={bestOperationDescription} />
          <Field label="Policy Date" value={policyDate} />
          <Field label="Legal Name" value={insuredInformation.legalName} />
          <Field label="DBA NAME" value={insuredInformation.dbaName} />
          <Field label="ENTITY TYPE" value={operations.entityType || insuredInformation.entityType} />
          <Field label="Trucks (Count)" value={limits?.nbrOfTrucks} format="number" />
          <Field label="(Auto Liability) Requested" value={hasAL} format="yn" />
          <Field label="(Automobile Physical Damage) Requested" value={hasAPD} format="yn" />
          <Field label="(Motor Truck Cargo) Requested" value={hasMTC} format="yn" />
          <Field label="(Truckers General Liability) Requested" value={hasTGL} format="yn" />
          <Field label="Owner Information First Name" value={owner.firstName} />
          <Field label="Owner Information Last Name" value={owner.lastName} />
          <Field label="Owner Information Date of Birth" value={owner.dob} format="date" />
          <Field label="Owner Information Street" value={`${owner.address?.address1 || ""} ${owner.address?.address2 || ""}`.trim()} />
          <Field label="Owner Information City" value={owner.address?.city} />
          <Field label="Owner Information State" value={owner.address?.state} />
          <Field label="Owner Information Zip" value={owner.address?.zip} />
          <Field label="INSURED PHONE NUMBER" value={insuredPhone} />
          <Field label="INSURED CONTACT EMAIL" value={insuredInformation.insuranceContactEmail} />
          <Field label="DATA SHARING OPTION" value={operations.nirvanaDataSharingOption || (operations.dataSharingOption ? TELEMETRY_OPTIONS[operations.dataSharingOption] || operations.dataSharingOption : null)} />
          <Field label="DATA SHARING PROVIDER" value={operations.nirvanaDataSharingProvider || operations.eldVendorName || operations.dashCamProviderText} />
        </Section>

        {renderLossBlocks()}

        {commodities.length > 0 && (
          <Section title="Commodities">
            {commodities.map((commodity, index) => (
              <div key={`${commodity.commodityKey || commodity.key}-${index}`} className="col-span-full bg-gray-50 p-3 rounded border">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Commodity" value={prettyLabel(commodity.commodityKey || commodity.key)} />
                  <Field label="Percentage" value={commodity.commodityPercentage} format="number" />
                  {commodity.generalMerchDetail && <Field label="Details" value={commodity.generalMerchDetail} />}
                </div>
              </div>
            ))}
          </Section>
        )}

        {vehicles.length > 0 && (
          <Section title="Vehicles">
            {vehicles.map((vehicle, index) => (
              <div key={vehicle.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border space-y-2">
                <Field label={`Vehicle ${index + 1} VIN`} value={vehicle.vin} />
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Year" value={vehicle.year} />
                  <Field label="Make" value={vehicle.make} />
                  <Field label="Model" value={vehicle.model} />
                  <Field label="Body Type" value={vehicle.bodyTypeKey || vehicle.bodyType} />
                  <Field label="Class" value={vehicle.classKey || vehicle.class} />
                  <Field label="Value" value={vehicle.value} format="currency" />
                  <Field label="Garaging ZIP" value={garageAddress.garageZip} />
                </div>
              </div>
            ))}
          </Section>
        )}

        <Section title="Radius">
          <Field label="0–50 miles" value={radius?.radius0_50} format="percentage" />
          <Field label="51–200 miles" value={radius?.radius51_200} format="percentage" />
          <Field label="201–500 miles" value={radius?.radius201_500} format="percentage" />
          <Field label="500+ miles" value={radius?.radius501} format="percentage" />
        </Section>

        {trailers.length > 0 && (
          <Section title="Trailers">
            {trailers.map((trailer, index) => (
              <div key={trailer.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border space-y-2">
                <Field label={`Trailer ${index + 1} VIN`} value={trailer.vin} />
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Value" value={trailer.value} format="currency" />
                  <Field label="Year" value={trailer.year} />
                  <Field label="Make" value={trailer.make} />
                  <Field label="Model" value={trailer.model} />
                  <Field label="Trailer Type" value={trailer.bodyTypeKey || trailer.bodyType} />
                </div>
              </div>
            ))}
          </Section>
        )}

        {drivers.length > 0 && (
          <Section title="Drivers">
            {drivers.map((driver, index) => (
              <div key={driver.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label={`Driver ${index + 1} License Number`} value={driver.licenseNumber} />
                  <Field label={`Driver ${index + 1} First Name`} value={driver.firstName} />
                  <Field label={`Driver ${index + 1} Last Name`} value={driver.lastName} />
                  <Field label={`Driver ${index + 1} License State`} value={driver.licenseState} />
                  <Field label={`Driver ${index + 1} Years Experience`} value={driver.yearsExperience} format="number" />
                  <Field label={`Driver ${index + 1} Date Of Birth`} value={driver.dateOfBirth} />
                  <Field label={`Driver ${index + 1} Date Of Hire`} value={driver.dateOfHire} />
                  <Field label={`Driver ${index + 1} Accidents`} value={driver.accidents} format="number" />
                  <Field label={`Driver ${index + 1} Violations`} value={driver.violations} format="number" />
                </div>
              </div>
            ))}
          </Section>
        )}

        <Section title="Coverages & Extras">
          {maybe(hasAL, <Field label="Auto Liability Limit for Cover Whale / Nirvana / Accelerant" value={limitAutoLiabilityOptionsForCoverWhaleAndNirvanaLabels[limits?.limitAutoLiability]} format="currency" />)}
          {maybe(hasAPD, <Field label="Comp/Collision Deductible Selected" value={coverage.compCollisionDeductibleChecked} format="yn" />)}
          <Field label="(Trailer Interchange) Requested" value={trailerInterchangeValue} format="yn" />
          {maybe(hasMTC, <Field label="(Motor Truck Cargo) Limit" value={limits?.mtcLimit || coverage.motor_truck_cargo_limit} format="currency" />)}
          {maybe(
            hasMTC,
            <Field
              label="(Motor Truck Cargo) Deductible"
              value={coverage.mtc_deductible ?? limits?.mtc_deductible}
              format="currency"
            />
          )}
          <Field label="Blanket Additional Insured" value={coverage.blanketAdditionalInsured === "yes" || coverage.blanketAdditionalInsured === true || coverage.blanketAdditionalInsured === "Y"} format="yn" />
          {maybe(hasAPD, <Field label="Rental Reimbursement" value={coverage.rentalReimbursement === "yes" || coverage.rentalReimbursement === true || coverage.rentalReimbursement === "Y"} format="yn" />)}
          <Field label="Waiver of Subrogation" value={limits?.insuredWaiverSubrogation === "Y"} format="yn" />
          <Field label="Refrigeration Coverage Required" value={hasRefrigeratedCommodity} format="yn" />
        </Section>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Section title="Initial Information">
        <Field label="Producer" value={quoteData?.producerData?.name || quoteData?.quote?.producer} />
        <Field label="Agent Email" value={quoteData?.agent_email || step1.agentEmail} />
        <Field label="Policy Date" value={(quoteData?.effectiveDate || quoteData?.effective_date)?.toString().split("T")[0] || ""} />
        <Field label="Carrier(s)" value={Array.isArray(quoteData?.carrier) ? quoteData?.carrier.join(", ") : quoteData?.carrier} />
        <Field label="Garage State" value={step1.garageState} />
        <Field label="Hazmat Placard" value={step1.hazmatPlacard} format="yn" />
        <Field label="Currently Insured with Progressive" value={step1.insuredWithProgressiveCommercialAuto} format="yn" />
        <Field label="Two or more years with the same DOT #" value={step1.operatedTwoYears} format="yn" />
        <Field label="Haul Double/Triple Trailers" value={step1.haulDoubleTripleTrailers} format="yn" />
        <Field label="Personal Use Of Trailer" value={step1.personalUseOfTrailer} format="yn" />
        <Field label="Personal Use Of Vehicle" value={topLevelOps.personalUseOfVehicle} format="yn" />
      </Section>

      <Section title="Insured">
        <Field label="Legal Name" value={insuredInformation.legalName} />
        <Field label="DBA" value={insuredInformation.dbaName} />
        <Field label="Entity Type" value={insuredInformation.entityType} />
        <Field label="FEIN" value={insuredInformation.fein} />
        <Field label="Revenue (Projected)" value={insuredInformation.revenue?.projected} format="currency" />
        <Field label="Revenue (Current)" value={insuredInformation.revenue?.current} format="currency" />
        <Field label="Revenue (1st Prior)" value={insuredInformation.revenue?.firstPrior} format="currency" />
        <Field label="Revenue (2nd Prior)" value={insuredInformation.revenue?.secondPrior} format="currency" />
        <Field label="Miles (Projected)" value={insuredInformation.miles?.projected} format="number" />
        <Field label="Miles (Current)" value={insuredInformation.miles?.current} format="number" />
        <Field label="Miles (1st Prior)" value={insuredInformation.miles?.firstPrior} format="number" />
        <Field label="Miles (2nd Prior)" value={insuredInformation.miles?.secondPrior} format="number" />
        <Field label="DOT Number" value={insuredInformation.dotNumber} />
        <Field label="MC Number" value={insuredInformation.motorCarrierNumber || "N/A"} />
        <Field
          label="Time In Business"
          value={
            (() => {
              const yearsVal =
                Number(insuredInformation.yearsInBusiness ?? quote?.yearsInBusiness ?? quote?.years_in_business) || 0;
              const monthsVal = Number(insuredInformation.monthsInBusiness) || 0;
              const monthsTotal = yearsVal * 12 + monthsVal;

              const years = Math.floor(monthsTotal / 12);
              const remainingMonths = monthsTotal % 12;

              return `${years} year${years !== 1 ? "s" : ""} ${remainingMonths} month${
                remainingMonths !== 1 ? "s" : ""
              }`;
            })()
          }
        />
        <Field label="Insurance Contact First Name" value={insuredInformation.insuranceContactFirstName} />
        <Field label="Insurance Contact Last Name" value={insuredInformation.insuranceContactLastName} />
        <Field label="Insurance Contact Email" value={insuredInformation.insuranceContactEmail} />
        <Field label="Insurance Contact Phone" value={insuredPhone} />
        <Field label="Has at least 3 years of loss runs" value={insuredInformation.hasLossRuns} format="yn" />
        <Field label="DOT # Issued Within the Last 60 Days" value={insuredInformation.insuredIssuedWithInLastSixtyDays} format="yn" />
      </Section>

      {nonEmpty(mailingAddress) && (
        <Section title="Mailing Address">
          <Field label="Street" value={`${mailingAddress.mailingStreet} ${mailingAddress.mailingStreet2 || ""}`.trim()} />
          <Field label="City" value={mailingAddress.mailingCity} />
          <Field label="State" value={mailingAddress.mailingState} />
          <Field label="Zip" value={mailingAddress.mailingZip} />
          <Field label="Country" value={mailingAddress.mailingCountry} />
        </Section>
      )}

      {nonEmpty(garageAddress) && (
        <Section title="Garaging Address">
          <Field label="Street" value={`${garageAddress.garageStreet} ${garageAddress.garageStreet2 || ""}`.trim()} />
          <Field label="City" value={garageAddress.garageCity} />
          <Field label="State" value={garageAddress.garageState} />
          <Field label="Zip" value={garageAddress.garageZip} />
          <Field label="Country" value={garageAddress.garageCountry} />
        </Section>
      )}

      {nonEmpty(owner) && (
        <Section title="Owner Information">
          <Field label="First Name" value={owner.firstName} />
          <Field label="Last Name" value={owner.lastName} />
          <Field label="Business Type" value={owner.businessType} />
          <Field label="Date of Birth" value={owner.dob} format="date" />

          {nonEmpty(owner.address) && (
            <div className="col-span-full">
              <Section title="Owner Address">
                <Field
                  label="Street"
                  value={`${owner.address.address1} ${owner.address.address2 || ""}`.trim()}
                />
                <Field label="City" value={owner.address.city} />
                <Field label="State" value={owner.address.state} />
                <Field label="Zip" value={owner.address.zip} />
              </Section>
            </div>
          )}
        </Section>
      )}

      {nonEmpty(shippingAddress) && (
        <Section title="Shipping Address">
          <Field label="Street" value={shippingAddress.shippingStreet} />
          <Field label="City" value={shippingAddress.shippingCity} />
          <Field label="State" value={shippingAddress.shippingState} />
          <Field label="Zip" value={shippingAddress.shippingZip} />
          <Field label="Country" value={shippingAddress.shippingCountry} />
        </Section>
      )}

      <Section title="Operations">
        <Field
          label="Operating Start"
          value={
            (() => {
              const start = operations.operatingStart ?? {};
              if (!start.year && !start.month) return "Not specified";

              const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
              ];

              const month =
                start.month && Number(start.month) >= 1 && Number(start.month) <= 12
                  ? monthNames[Number(start.month) - 1]
                  : null;

              return month ? `${month} ${start.year}` : start.year ?? "Not specified";
            })()
          }
        />
        <Field label="Operation Description" value={operations.operationDescription} />
        <Field label="Best description of your operation" value={bestOperationDescription} />
        <Field label="Ineligible Operations" value={operations.ineligibleOperations === "Y"} format="yn" />
        <Field label="Prior Insurance Cancelled/Non-Renewed" value={operations.priorInsuranceCancelledNonrenewed === "Y"} format="yn" />
        <Field label="Cancellations (12mo)" value={operations.cancellations12mo === "Y"} format="yn" />
        <Field label="Bankruptcies" value={operations.bankruptcies === "Y"} format="yn" />
        <Field label="Personal Use Of Vehicle" value={operations.personalUseOfVehicle === "Y"} format="yn" />
        <Field label="UIIA Intermodal" value={operations.UIIAintermodal === "Y"} format="yn" />
        <Field label="Filings AL Federal" value={operations.filingsAlFederal === "Y"} format="yn" />
        <Field label="Filings AL State" value={operations.filingsAlState === "Y"} format="yn" />
        <Field label="Load/Unload at Roadside Construction Sites" value={operations.roadsideConstruction === "Y"} format="yn" />
        <Field label="Dump Loads Fully Tarped" value={operations.dumpLoadsFullyTarped === "Y"} format="yn" />
        <Field label="Drivers Required To Maintain 6 Inches of Free Board" value={operations.driversMaintainSixInchesFreeBoard === "Y"} format="yn" />
        <Field label="Oversized Shipments" value={operations.oversizedShipments === "Y"} format="yn" />
        <Field label="Owner Operators" value={operations.ownerOperators === "Y"} format="yn" />
        <Field label="Interline Agreement" value={operations.interlineAgreement === "Y"} format="yn" />
        <Field label="Interchange Agreement" value={operations.interchangeAgreement === "Y"} format="yn" />
        <Field label="Intermodal Agreement" value={operations.intermodalAgreement === "Y"} format="yn" />
        <Field label="Lease Vehicles/Trailers" value={operations.leaseVehiclesTrailers === "Y"} format="yn" />
        <Field label="Travel to Mexico" value={operations.travelMexico === "Y"} format="yn" />
        <Field label="Travel to Canada" value={operations.travelCanada === "Y"} format="yn" />
        <Field label="Team Drivers" value={operations.teamDrivers === "Y"} format="yn" />
        <Field label="Guest Passengers" value={operations.guestPassengers === "Y"} format="yn" />
        <Field label="Brokerage Authority" value={operations.brokerageAuthority === "Y"} format="yn" />
        <Field label="Subsidiary" value={operations.subsidiary === "Y"} format="yn" />
        <Field label="Trip Leasing Under Authority" value={operations.tripLeasingUnderAuthority === "Y"} format="yn" />
      </Section>

      <Section title="Data Sharing">
        <Field label="Telemetry Option" value={operations.dataSharingOption ? TELEMETRY_OPTIONS[operations.dataSharingOption] : (operations.nirvanaDataSharingOption || null)} />
        <Field label="Telemetry Provider" value={operations.eldVendorName || operations.dashCamProviderText || operations.nirvanaDataSharingProvider || null} />
      </Section>

      {Array.isArray(drivers) && drivers.length > 0 && (
        <Section title="Drivers">
          <div className="col-span-full bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold mb-2">Driver Training & Safety</div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Field label="Way of Compensation" value={driverTraining.wayOfCompensation} />
              <Field label="Company Rules/Procedures" value={driverTraining.companyRulesProcedures} format="yn" />
              <Field label="Daily Vehicle Inspections" value={driverTraining.dailyVehicleInspections} format="yn" />
              <Field label="Equipment Familiarization" value={driverTraining.equipmentFamiliarization} format="yn" />
              <Field label="Route Familiarization" value={driverTraining.routeFamiliarization} format="yn" />
              <Field label="Emergency Procedures" value={driverTraining.emergencyProcedures} format="yn" />
              <Field label="Accident Reporting Procedures" value={driverTraining.accidentReportingProcedures} format="yn" />
              <Field label="Written Driver Safety Manual" value={driverTraining.writtenDriverSafetyManual} format="yn" />
              <Field label="MVRs Checked Annually" value={driverTraining.mvrsCheckedAnnually} format="yn" />
              <Field label="Driver Home Days/Week" value={driverTraining.driverHomeDaysPerWeek} format="number" />
              <Field label="Drivers DOT Compliant" value={driverTraining.driversDotCompliant} format="yn" />
              <Field label="Minimum Age of Drivers" value={driverTraining.minimumAgeOfDrivers} format="number" />
              <Field label="Violations Past 36 Months" value={driverTraining.violationsPastSixMonths} format="number" />
              <Field label="Zero Tolerance Policy" value={driverTraining.zeroTolerancePolicyForMajorViolations} format="yn" />
              <Field label="Safety Rewards Program" value={driverTraining.safetyRewardsProgram} format="yn" />
              <Field label="Disciplinary Action" value={driverTraining.disciplinaryActionForDrivers} />
              <Field label="Mandatory Safety Meetings Held" value={driverTraining.howOftenAreMandatorySafetyMeetingsHeld} />
              <Field label="Maintain Accident Register" value={driverTraining.maintainAccidentRegister} format="yn" />
              <Field label="Written Maintenance Program" value={driverTraining.writtenMaintenanceProgram} format="yn" />
              <Field label="Insured Does Own Maintenance" value={driverTraining.insuredDoTheirOwnMaintenance} format="yn" />
              <Field label="CDL Experience Year" value={driverTraining.cdlExperience?.year} />
              <Field label="CDL Experience Month" value={driverTraining.cdlExperience?.month} />
            </div>
          </div>

          {drivers.map((driver, index) => (
            <div key={driver.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <Field label="First Name" value={driver.firstName} />
                <Field label="Last Name" value={driver.lastName} />
                <Field label="Date Of Birth" value={driver.dateOfBirth} />
                <Field label="Date Of Hire" value={driver.dateOfHire} />
                <Field label="Years Experience" value={driver.yearsExperience} format="number" />
                <Field label="License State" value={driver.licenseState} />
                <Field label="License Number" value={driver.licenseNumber} />
                <Field label="Accidents" value={driver.accidents} format="number" />
                <Field label="Violations" value={driver.violations} format="number" />
                <Field label="Suspensions" value={driver.suspensions} format="number" />
                <Field label="Eligibility" value={driver.eligibility} />
                <Field label="Exclude From Policy" value={driver.exclude} format="yn" />
                <Field label="Status" value={driver.status} />
              </div>
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(vehicles) && vehicles.length > 0 && (
        <Section title="Vehicles">
          <div className="col-span-full bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold mb-2">Vehicle Summary</div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Field label="Personal Use" value={personalUse} format="yn" />
              <Field label="Vehicle Files DOT Compliant" value={filesDot} format="yn" />
            </div>
          </div>
          {vehicles.map((vehicle, index) => (
            <div key={vehicle.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <Field label="Year" value={vehicle.year} />
                <Field label="Make" value={vehicle.make} />
                <Field label="Model" value={vehicle.model} />
                <Field label="VIN" value={vehicle.vin} />
                <Field label="Value" value={vehicle.value} format="currency" />
                <Field label="Body Type" value={vehicle.bodyTypeKey || vehicle.bodyType} />
                <Field label="Class" value={vehicle.classKey || vehicle.class} />
                <Field label="Include APD Towing" value={vehicle.includeAPDTowing ?? vehicle.includeApdTowing} format="yn" />
                <Field label="Hitch Type" value={vehicle.hitchType} />
              </div>
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(trailers) && trailers.length > 0 && (
        <Section title="Trailers">
          {trailers.map((trailer, index) => (
            <div key={trailer.id || index} className="col-span-full bg-gray-50 p-4 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <Field label="Year" value={trailer.year} />
                <Field label="Make" value={trailer.make} />
                <Field label="Model" value={trailer.model} />
                <Field label="VIN" value={trailer.vin} />
                <Field label="Value" value={trailer.value} format="currency" />
                <Field label="Body Type" value={trailer.bodyTypeKey || trailer.bodyType} />
                <Field label="Deductible Type" value={trailer.deductibleType} />
                <Field label="Deductible" value={trailer.deductible} format="currency" />
              </div>
            </div>
          ))}
        </Section>
      )}

      <Section title="Coverages">
        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Auto Liability</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Requested" value={hasAL} format="yn" />
            {maybe(hasAL, <Field label="Auto Liability Limit for Cover Whale / Nirvana / Accelerant" value={limitAutoLiabilityOptionsForCoverWhaleAndNirvanaLabels[limits?.limitAutoLiability]} format="currency" />)}
            {maybe(hasAL, <Field label="Auto Liability Limit for Progressive" value={limitAutoLiabilityOptionsForProgressiveLabels[limits?.autoLiabilityLimitForProgressive]} format="currency" />)}
            {maybe(hasAL, <Field label="PIP" value={coverage.optAlPip} format="yn" />)}
            {maybe(hasAL, <Field label="UM/UIM Property Damage" value={coverage.optAlUm} format="yn" />)}
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Automobile Physical Damage (APD)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Requested" value={hasAPD} format="yn" />
            {maybe(hasAPD, <Field label="Comp/Collision Deductible Selected" value={coverage.compCollisionDeductibleChecked} format="yn" />)}
            {maybe(hasAPD && coverage.compCollisionDeductibleOption, (
              <Field label="Comp/Collision Deductible Option" value={coverage.compCollisionDeductibleOption} format="currency" />
            ))}
            {maybe(hasAPD, <Field label="Rental Reimbursement" value={coverage.rentalReimbursement === "yes" || coverage.rentalReimbursement === true || coverage.rentalReimbursement === "Y"} format="yn" />)}
            {maybe(hasAPD, <Field label="Roadside Assistance" value={coverage.roadsideAssistance === "yes" || coverage.roadsideAssistance === true || coverage.roadsideAssistance === "Y"} format="yn" />)}
            {maybe(hasAPD, <Field label="Blanket Additional Insured" value={coverage.blanketAdditionalInsured === "yes" || coverage.blanketAdditionalInsured === true || coverage.blanketAdditionalInsured === "Y"} format="yn" />)}
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Motor Truck Cargo</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Requested" value={hasMTC} format="yn" />
            {maybe(hasMTC, <Field label="Limit" value={limits?.mtcLimit || coverage.motor_truck_cargo_limit} format="currency" />)}
            {maybe(hasMTC && coverage.mtc_deductible, <Field label="Deductible" value={coverage.mtc_deductible} format="currency" />)}
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Truckers General Liability</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Requested" value={hasTGL} format="yn" />
            {maybe(hasTGL, <Field label="Primary & Noncontributory (TGL)" value={limits?.primaryNoncontributoryTGL === "Y"} format="yn" />)}
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Non-Trucking Liability</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Requested" value={hasNTL} format="yn" />
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Trailer Interchange</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Requested" value={trailerInterchangeValue} format="yn" />
            {maybe(hasTI, <Field label="Limit" value={coverage.trailer_interchange_limit || limits?.trailerInterchangeLimit} format="currency" />)}
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Hired Auto Liability</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Selected" value={coverage.hiredAutoLiability} format="yn" />
            {maybe(coverage.hiredAutoLiability, <Field label="Count (Past Year)" value={coverage.hiredAutoCount} format="number" />)}
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Limits & Values</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Trucks (Count)" value={limits?.nbrOfTrucks} format="number" />
            <Field label="Trucks (Total Value)" value={limits?.valueOfTrucks} format="currency" />
            <Field label="Trailers (Count)" value={limits?.nbrOfTrailers} format="number" />
            <Field label="Trailers (Total Value)" value={limits?.valueOfTrailers} format="currency" />

            <Field label="Waiver of Subrogation" value={limits?.insuredWaiverSubrogation === "Y"} format="yn" />
            <Field label="Primary & Noncontributory (AL)" value={limits?.primaryNoncontributoryAL === "Y"} format="yn" />

            <Field label="Towing / Labor / Storage Limit"
              value={quote?.coverage?.limits?.towingLimit || quote?.limits?.limitTowingStorage}
              format="currency"
            />
            <Field label="Trailer Interchange" value={trailerInterchangeValue} format="yn" />
          </div>
        </div>

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Insurance History</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Currently Insured" value={coverage.currentlyInsured} format="yn" />
            <Field label="Continuous Coverage" value={coverage.continuousCoverage} format="yn" />
            <Field label="Current BI Limit (Checked)" value={coverage.currentBILimitChecked} format="yn" />
            {maybe(coverage.currentBILimitValue, (
              <Field
                label="Current BI Limit (Value)"
                value={AL_LIMIT_LABELS[coverage.currentBILimitValue] || coverage.currentBILimitValue}
                format="currency"
              />
            ))}
          </div>
        </div>

        {coverage.nonOwnedTrailerCoverage === "yes" && (
          <div className="col-span-full bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold mb-2">Non-Owned Trailer Coverage</div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Field label="Count" value={coverage.nonOwnedTrailersCount} format="number" />
            </div>
            {nonOwnedTrailers.length > 0 && (
              <div className="mt-3 space-y-3">
                {nonOwnedTrailers.map((trailer, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded border">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      <Field label="Trailer Type" value={NON_OWNED_TRAILER_TYPE[trailer.trailerType] || trailer.trailerType || "N/A"} />
                      <Field
                        label="Coverage Amount"
                        value={trailer.coverageAmount}
                        format="currency"
                      />
                      {maybe(trailer.deductible, (
                        <Field label="Deductible" value={trailer.deductible} format="currency" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="col-span-full bg-white border rounded-lg p-4">
          <div className="text-sm font-semibold mb-2">Radius & Mileage</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="0–50 miles" value={radius?.radius0_50} format="percentage" />
            <Field label="51–200 miles" value={radius?.radius51_200} format="percentage" />
            <Field label="201–500 miles" value={radius?.radius201_500} format="percentage" />
            <Field label="500+ miles" value={radius?.radius501} format="percentage" />
            <Field label="State Operation" value={radius?.stateOperation} />
            <Field label="Annual Mileage" value={radius?.annualMileage} format="number" />
          </div>
        </div>

        {Array.isArray(commodities) && commodities.length > 0 && (
          <div className="col-span-full bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold mb-2">Commodities</div>
            <div className="grid grid-cols-1 gap-3">
              {commodities.map((commodity, index) => (
                <div key={`${commodity.commodityKey}-${index}`} className="bg-gray-50 p-3 rounded border">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <Field label="Commodity" value={prettyLabel(commodity.commodityKey || commodity.key)} />
                    <Field label="Percentage" value={commodity.commodityPercentage} format="number" />
                    {commodity.generalMerchDetail && (
                      <Field label="Details" value={commodity.generalMerchDetail} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stateMileage.length > 0 && (
          <div className="col-span-full bg-white border rounded-lg p-4">
            <div className="text-sm font-semibold mb-2">State Mileage</div>
            <div className="grid grid-cols-1 gap-3">
              {stateMileage.map((state, index) => (
                <div key={`${state.state}-${index}`} className="bg-gray-50 p-3 rounded border">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <Field label="State" value={state.state} />
                    <Field label="Percentage" value={state.percentage} format="number" />
                    {state.miles != null && state.miles !== "" && (
                      <Field label="Miles" value={state.miles} format="number" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      {!hasAccelerant && renderLossBlocks()}

      {hasAccelerant && (
        <Section title="Documents (Accelerant)">
          <div className="col-span-full bg-white border rounded-lg p-4 space-y-4">
            <div className="text-sm font-semibold">Uploaded Documents</div>
            {uploadedDocs.length === 0 && (
              <p className="text-sm text-gray-600">No documents were uploaded in Step 10.</p>
            )}
            <ul className="space-y-2">
              {uploadedDocs.map((doc, idx) => (
                <li key={`${doc.type}-${idx}`} className="flex items-center justify-between gap-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-900">{(doc.name || doc.type || "").toString().toUpperCase()}</span>
                    <span className="text-xs text-gray-500">{doc.type}</span>
                  </div>
                  {doc.s3_link || doc.url ? (
                    <button
                      type="button"
                      className="text-blue-600 text-sm underline break-all"
                      onClick={() => handleDocDownload(doc)}
                    >
                      Download
                    </button>
                  ) : (
                    <span className="text-xs text-gray-500">No link available</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-full bg-white border rounded-lg p-4 space-y-3">
            <div className="text-sm font-semibold">Schedules</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => handleDownload("vehiclesAndTrailers")}
                className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 text-sm"
              >
                Download Vehicle & Trailer Schedule
              </button>
              <button
                onClick={() => handleDownload("drivers")}
                className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 text-sm"
              >
                Download Driver Schedule
              </button>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}

/** ---------- Public Component ---------- */
export function ConnectedQuoteView({ quoteData, selectedCarrier }) {
  // Expecting `quoteData.quote` shape (the one you build in mapData)
  // Sections in your desired order:
  return (
    <QuoteDetails quoteData={quoteData} selectedCarrier={selectedCarrier} />
  );
}
